import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ESTIMATE_STATUS_LABELS,
  formatCents,
  formatDate,
  type DiscountType,
  type EstimateStatus,
  type LineItemInput,
} from '@fsm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DeleteRecord } from '../components/Selection';
import { DepositCard, type DepositDraft } from '../components/DepositCard';
import { keys } from '../lib/queries';
import { LineItemEditor } from '../components/LineItemEditor';
import { DocumentActions } from '../components/DocumentActions';
import { ContactCard } from '../components/ContactCard';
import { Icon } from '../components/icons';
import { PageFooter, useCrumb } from '../components/Layout';
import {
  ActivityFeed,
  FLOW_ICONS,
  FlowBar,
  flowIcon,
  RecordHeader,
  shortDate,
  type FeedEntry,
  type FlowStep,
} from '../components/record';
import { Badge, Button, Callout, Card, Dot, Spinner, SummaryBand, Toast } from '../components/ui';

/**
 * The estimate page.
 *
 * Same three columns as the job page, for the same reason: left is who and
 * where, the middle is the thing itself in the order it moves, right is
 * history. An estimate's life is shorter than a job's — it goes out, and then
 * the customer says yes or no — so the flow bar has two stages rather than six.
 */
export function EstimateDetailPage() {
  const { can } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<LineItemInput[]>([]);
  const [discount, setDiscount] = useState<{
    type: DiscountType;
    valueCents: number;
    valueBps: number;
  }>({ type: 'none', valueCents: 0, valueBps: 0 });
  const [deposit, setDeposit] = useState<DepositDraft>({
    type: 'none',
    valueCents: 0,
    valueBps: 0,
  });
  const [toast, setToast] = useState<{ message: string; tone: 'error' | 'success' } | null>(null);
  const [emailNote, setEmailNote] = useState<string | null>(null);

  const { data: estimate, isLoading } = useQuery({
    queryKey: keys.estimate(id ?? ''),
    queryFn: () => api.estimates.get(id!),
    enabled: Boolean(id),
  });

  useCrumb(estimate ? `Estimate #${estimate.number}` : null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.estimate(id!) });
    void qc.invalidateQueries({ queryKey: ['estimates'] });
  };

  const fail = (e: unknown, fallback: string) =>
    setToast({ message: (e as { message?: string }).message ?? fallback, tone: 'error' });

  const setStatus = useMutation({
    mutationFn: (status: EstimateStatus) => api.estimates.setStatus(id!, status),
    onSuccess: refresh,
    onError: (e) => fail(e, 'Could not update'),
  });

  const save = useMutation({
    mutationFn: () =>
      api.estimates.update(id!, {
        lineItems: draft,
        discountType: discount.type,
        discountValueCents: discount.valueCents,
        discountValueBps: discount.valueBps,
        depositType: deposit.type,
        depositValueCents: deposit.valueCents,
        depositValueBps: deposit.valueBps,
      }),
    onSuccess: () => {
      setEditing(false);
      refresh();
    },
    onError: (e) => fail(e, 'Could not save'),
  });

  const sendEmail = useMutation({
    mutationFn: () => api.estimates.email(id!),
    onSuccess: (result) => {
      setEmailNote(
        result.ok
          ? `Sent to ${result.to}${result.pdfAttached ? ' with the PDF attached' : ' — the PDF could not be generated, so the email carries the link only'}`
          : `Could not send: ${result.failureReason ?? 'unknown error'}`,
      );
      // The send promotes a draft to sent, so the flow bar is now stale.
      refresh();
    },
    onError: (error: Error) => setEmailNote(`Could not send: ${error.message}`),
  });

  const convert = useMutation({
    mutationFn: () => api.estimates.convert(id!),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      navigate(`/jobs/${result.job.id}`);
    },
    onError: (e) => fail(e, 'Could not convert'),
  });

  if (isLoading || !estimate) {
    return (
      <div className="grid flex-1 place-items-center">
        <Spinner className="size-6 text-ink-4" />
      </div>
    );
  }

  const locked = Boolean(estimate.convertedJobId) || estimate.status === 'approved';
  const declined = estimate.status === 'declined';
  const hasEmail = Boolean(estimate.customer?.email);

  /*
   * Sent, then approved. Two stages because that is the whole of an estimate:
   * everything before "sent" is a draft nobody has seen, and everything after
   * "approved" is a job.
   */
  const flowSteps: FlowStep[] = [
    {
      key: 'sent',
      label: 'Sent',
      icon: flowIcon(FLOW_ICONS.send),
      done: Boolean(estimate.sentAt) || estimate.status !== 'draft',
      sub: estimate.sentAt ? shortDate(estimate.sentAt) : hasEmail ? 'Email it' : 'No address',
      onClick: estimate.status === 'draft' && hasEmail ? () => sendEmail.mutate() : null,
    },
    {
      key: 'approved',
      label: declined ? 'Declined' : 'Approved',
      icon: flowIcon(FLOW_ICONS.approve),
      done: estimate.status === 'approved',
      failed: declined,
      sub: estimate.decidedAt
        ? shortDate(estimate.decidedAt)
        : estimate.validUntil
          ? `Valid to ${shortDate(estimate.validUntil)}`
          : null,
      onClick: estimate.status === 'sent' ? () => setStatus.mutate('approved') : null,
    },
  ];

  const feedEntries: FeedEntry[] = [
    {
      id: 'created',
      at: estimate.createdAt,
      tone: 'status',
      title: 'Estimate created',
      meta: '',
    },
    ...(estimate.sentAt
      ? [
          {
            id: 'sent',
            at: estimate.sentAt,
            tone: 'status' as const,
            title: 'Sent to the customer',
            meta: estimate.customer?.email ?? '',
          },
        ]
      : []),
    ...(estimate.decidedAt
      ? [
          {
            id: 'decided',
            at: estimate.decidedAt,
            tone: (estimate.status === 'approved' ? 'money' : 'muted') as 'money' | 'muted',
            title:
              estimate.status === 'approved' ? 'Customer approved' : 'Customer declined',
            meta: '',
          },
        ]
      : []),
  ];

  return (
    <>
      <RecordHeader
        eyebrow="Estimate"
        number={`#${estimate.number}`}
        title={estimate.title}
        badges={
          <>
            <Badge tone={estimate.status === 'approved' ? 'emerald' : declined ? 'red' : 'slate'}>
              {ESTIMATE_STATUS_LABELS[estimate.status]}
            </Badge>
            {/*
              Where it was quoted, and what it became — two different jobs,
              so two different badges. "Quoted at" is the visit a technician
              raised it on; "Open the job" is the work an approved estimate
              turned into. Showing either as simply "the job" would make the
              two indistinguishable on an estimate that has both.
            */}
            {estimate.jobId && (
              <Link to={`/jobs/${estimate.jobId}`}>
                <Badge tone="slate">Quoted at a job ↗</Badge>
              </Link>
            )}
            {estimate.convertedJobId && (
              <Link to={`/jobs/${estimate.convertedJobId}`}>
                <Badge tone="violet">Open the job ↗</Badge>
              </Link>
            )}
          </>
        }
        subtitle={
          <>
            <Icon name="users" size={14} className="text-ink-4" />
            <Link to={`/customers/${estimate.customerId}`} className="hover:text-accent">
              {estimate.customer?.name}
            </Link>
            <Dot />
            {estimate.validUntil
              ? `Valid until ${formatDate(estimate.validUntil)}`
              : `Created ${formatDate(estimate.createdAt)}`}
          </>
        }
        /*
         * Sending and approving are on the flow bar. What is left up here is
         * everything that is not a stage: the document itself, converting, and
         * the one negative outcome, which should never be as easy to reach as
         * the positive one.
         */
        actions={
          editing ? (
            <>
              <Button onClick={() => setEditing(false)}>Cancel</Button>
              <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
            </>
          ) : (
            <>
              <DocumentActions
                doc={{
                  label: `Estimate #${estimate.number}`,
                  filename: `estimate-${estimate.number}.pdf`,
                  html: () => api.estimates.printHtml(estimate.id),
                  pdf: () => api.estimates.pdf(estimate.id),
                }}
                onError={(message) => setToast({ message, tone: 'error' })}
              />
              {estimate.status === 'sent' && (
                <Button
                  variant="danger"
                  loading={setStatus.isPending && setStatus.variables === 'declined'}
                  onClick={() => setStatus.mutate('declined')}
                >
                  Mark declined
                </Button>
              )}
              {estimate.status === 'approved' && !estimate.convertedJobId ? (
                <Button
                  variant="primary"
                  icon="job"
                  loading={convert.isPending}
                  onClick={() => convert.mutate()}
                >
                  Convert to job
                </Button>
              ) : (
                <Button
                  variant="primary"
                  icon="mail"
                  loading={sendEmail.isPending}
                  disabled={!hasEmail}
                  title={hasEmail ? `Email it to ${estimate.customer?.email}` : 'No email on file'}
                  onClick={() => sendEmail.mutate()}
                >
                  Email estimate
                </Button>
              )}
            </>
          )
        }
      />

      {emailNote && (
        <div className="mx-auto w-full max-w-[1860px] px-[30px] pb-4">
          <Callout tone="info" icon="mail">
            {emailNote}
          </Callout>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto px-[30px]">
        <div className="mx-auto grid max-w-[1860px] grid-cols-1 items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)] xl:grid-cols-[minmax(265px,0.9fr)_minmax(560px,2.6fr)_minmax(280px,1fr)]">
          {/* ---------------------------------------------------- left rail */}
          <div className="space-y-5">
            <ContactCard
              name={estimate.customer?.name ?? 'Customer'}
              role="Residential customer"
              phone={estimate.customer?.phone}
              email={estimate.customer?.email}
              property={estimate.property}
              customerId={estimate.customerId}
              footLink={{ to: `/customers/${estimate.customerId}`, label: 'View customer profile' }}
            />

            <Card
              title="Terms"
              action={<Icon name="calendar" size={17} className="text-ink-4" />}
              bodyClassName="px-[21px] pt-1 pb-[21px]"
            >
              <dl className="text-[14px]">
                <Row label="Created" value={formatDate(estimate.createdAt)} />
                <Row
                  label="Valid until"
                  value={estimate.validUntil ? formatDate(estimate.validUntil) : 'Not set'}
                />
                <Row label="Tax rate" value={`${(estimate.taxRateBps / 100).toFixed(2)}%`} />
              </dl>
              {estimate.notes && (
                <p className="mt-4 border-t border-line pt-4 text-[13px] leading-relaxed whitespace-pre-wrap text-ink-2">
                  {estimate.notes}
                </p>
              )}
            </Card>
          </div>

          {/* ------------------------------------------------------- middle */}
          <div className="space-y-5">
            <FlowBar
              steps={flowSteps}
              busy={setStatus.isPending || sendEmail.isPending}
              title="Estimate progress"
              status={
                declined
                  ? 'Customer declined'
                  : estimate.status === 'approved'
                    ? 'Approved — ready to convert'
                    : estimate.status === 'sent'
                      ? 'Waiting on the customer'
                      : 'Ready when you are'
              }
            />

            <Card
              title="Line items"
              icon="file"
              count={estimate.lineItems?.length ?? 0}
              divided
              bodyClassName="p-0"
              action={
                !editing &&
                !locked && (
                  <button
                    type="button"
                    className="text-[13px] font-semibold text-accent hover:underline"
                    onClick={() => {
                      setDraft(
                        (estimate.lineItems ?? []).map((li) => ({
                          priceBookItemId: li.priceBookItemId,
                          name: li.name,
                          description: li.description,
                          kind: li.kind,
                          quantity: li.quantity,
                          unitPriceCents: li.unitPriceCents,
                          taxable: li.taxable,
                          position: li.position,
                        })),
                      );
                      setDiscount({
                        type: estimate.discountType,
                        valueCents: estimate.discountValueCents,
                        valueBps: estimate.discountValueBps,
                      });
                      setDeposit({
                        type: estimate.depositType,
                        valueCents: estimate.depositValueCents,
                        valueBps: estimate.depositValueBps,
                      });
                      setEditing(true);
                    }}
                  >
                    Edit items
                  </button>
                )
              }
              band={
                <SummaryBand
                  label="Estimate total"
                  caption={
                    estimate.status === 'approved'
                      ? 'Approved by the customer'
                      : declined
                        ? 'Declined by the customer'
                        : 'Awaiting customer approval'
                  }
                  amount={formatCents(estimate.totals?.totalCents ?? 0)}
                />
              }
            >
              <LineItemEditor
                value={
                  editing
                    ? draft
                    : (estimate.lineItems ?? []).map((li) => ({
                        priceBookItemId: li.priceBookItemId,
                        name: li.name,
                        description: li.description,
                        kind: li.kind,
                        quantity: li.quantity,
                        unitPriceCents: li.unitPriceCents,
                        taxable: li.taxable,
                        position: li.position,
                      }))
                }
                onChange={setDraft}
                taxRateBps={estimate.taxRateBps}
                readOnly={!editing}
                hideTotal
                discount={{
                  type: editing ? discount.type : estimate.discountType,
                  valueCents: editing ? discount.valueCents : estimate.discountValueCents,
                  valueBps: editing ? discount.valueBps : estimate.discountValueBps,
                  onChange: setDiscount,
                }}
              />
            </Card>
          </div>

          {/* --------------------------------------------------- right rail */}
          <div className="space-y-5">
            <DepositCard
              estimate={estimate}
              editing={editing}
              draft={deposit}
              onChange={setDeposit}
            />
            <ActivityFeed entries={feedEntries} empty="Nothing has happened to this estimate yet." />
          </div>
        </div>

        {can('admin', 'dispatcher') && (
          <DeleteRecord
            one="estimate"
            many="estimates"
            warning="Its line items go with it. An estimate that has already become a job cannot be deleted."
            onConfirm={() => api.estimates.bulkRemove([estimate.id])}
            onDeleted={() => {
              void qc.invalidateQueries({ queryKey: ['estimates'] });
              navigate('/estimates');
            }}
          />
        )}

        <PageFooter label={`Estimate #${estimate.number}`} />
      </div>

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="my-[13px] flex items-center justify-between gap-3 first:mt-0 last:mb-0">
      <dt className="text-ink-3">{label}</dt>
      <dd className="tnum">{value}</dd>
    </div>
  );
}
